import type { MetricName } from "schemas/generated/analyticsRead";
import type { Workspace } from "schemas/generated/workspace";
import type { ChartInterval } from "schemas/utils/analytics";

import * as React from "react";

import { AnalyticsPageHeader } from "@editor/components/analytics/AnalyticsPageHeader";
import { ConditionallyBlurredComponent } from "@editor/components/analytics/ConditionallyBlurredComponent";
import MetricWithDelta from "@editor/components/analytics/MetricWithDelta";
import { Loader } from "@editor/components/common/Loader";
import { useCurrentWorkspaceContext } from "@editor/contexts/WorkspaceDashboardContext";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import {
  calculateDelta,
  formatValueToTableCell,
} from "@editor/utils/analytics";

import AnalyticsFilters from "@/features/analytics/AnalyticsFilters";
import { getChartIntervalOptions } from "@/features/analytics/chart";
import { DETAILS_PAGE_CARD_METRICS } from "@/features/analytics/constants";
import { useAnalyticsQueryContext } from "@/features/analytics/contexts/AnalyticsQueryContext";
import useAnalyticsStatus from "@/features/analytics/useAnalyticsStatus";
import useBasicAnalyticsRead from "@/features/analytics/useBasicAnalyticsRead";
import { Combobox } from "@replo/design-system/components/combobox/Combobox";
import PageHeader from "@replo/design-system/components/PageHeader";
import { NoChartData } from "@replo/design-system/components/shadcn/NoChartData";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { useNavigate } from "react-router-dom";
import { ConditionOperatorEnum } from "schemas/analyticsRead";

import { AnalyticsEmptyState } from "./AnalyticsEmptyState";
import { ReploLineChart } from "./ReploLineChart";

const PageDetails: React.FC = () => {
  const { workspace, isLoading } = useCurrentWorkspaceContext();

  if (!workspace) {
    return null;
  }

  if (isLoading) {
    return <Loader />;
  }

  return <LoadedPageDetails workspace={workspace} />;
};

type LoadedPageDetailsProps = {
  workspace: Workspace;
};

const LoadedPageDetails: React.FC<LoadedPageDetailsProps> = ({ workspace }) => {
  const { query, dispatchAnalyticsQuery, urlHostsObj } =
    useAnalyticsQueryContext();
  const analytics = useLogAnalytics();
  const timeZone =
    workspace.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

  const navigate = useNavigate();
  const { dataDisplayType } = useAnalyticsStatus();

  /**
   * NOTE (Max, 2024-12-15): When landing on the deep dive page,
   * the query should already have the EQUALS urlPath filter.
   * If it doesn't, it means that we clicked the "Deep Dive" tab on the
   * DashboardMenuItems, in which case we just default to "/".
   *
   * If we default to "/", we'll need to update query's urlPath filters,
   * which is what we do in the useEffect() below.
   */
  const pageUrlPath = React.useMemo(
    () =>
      query.filters.urlPath.find(
        (filter) => filter.operator === ConditionOperatorEnum.EQUALS,
      )?.value[0] ?? "/",
    [query.filters.urlPath],
  );

  const { intervalOptions } = getChartIntervalOptions(
    query.selectedTimePeriod,
    timeZone,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies:
  React.useEffect(() => {
    dispatchAnalyticsQuery({
      type: "initDeepDivePage",
      payload: {
        urlPath: pageUrlPath,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { mainRangeResults, compareAtRangeResults, isLoading } =
    useBasicAnalyticsRead({
      type: "withUrlSyncQuery",
      queryConfig: {
        useChartInterval: false,
      },
    });

  const {
    mainRangeResults: chartMainRangeResults,
    compareAtRangeResults: chartCompareAtRangeResults,
    isLoading: isChartResultsLoading,
    constructedQuery: chartQuery,
  } = useBasicAnalyticsRead({
    type: "withUrlSyncQuery",
    queryConfig: {
      useChartInterval: true,
    },
  });

  const handleIntervalChange = (newInterval: ChartInterval) => {
    dispatchAnalyticsQuery({
      type: "updateChartInterval",
      payload: newInterval,
    });
  };

  const selectedIntervalOption = intervalOptions.find(
    (option) => option.value === query.chartInterval,
  );

  const pageTitle = mainRangeResults[0]?.metadata.title;

  const metricsToPlot: {
    key: MetricName;
    label: string;
  }[] = [
    {
      key: "conversion_rates",
      label: "Conversion Rate",
    },
    {
      key: "unique_sessions",
      label: "Total Sessions",
    },
    {
      key: "average_order_value",
      label: "Average Order Value",
    },
    {
      key: "revenue_per_session",
      label: "Revenue Per Session",
    },
  ];

  return (
    <div className="flex flex-col gap-4 overflow-hidden">
      <AnalyticsPageHeader
        header={
          <PageHeader
            title={pageTitle ?? "Page Details"}
            divider={false}
            onBackClick={() => {
              navigate(-1);
            }}
          />
        }
      />
      <div className="flex flex-col h-full overflow-auto gap-2">
        <AnalyticsFilters />
        {isLoading && urlHostsObj.isLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {DETAILS_PAGE_CARD_METRICS.map((metric, index) => {
              const metricData =
                mainRangeResults[0]?.metrics[metric.key as MetricName];
              const compareAtMetricData =
                compareAtRangeResults[0]?.metrics[metric.key as MetricName];
              const delta = calculateDelta(
                metricData?.[0],
                compareAtMetricData?.[0],
              );
              const doesCompareValueExist = (compareAtMetricData?.[0] ?? 0) > 0;

              return (
                <div
                  key={index}
                  className="border-border rounded-xl border p-6"
                >
                  <div className="flex flex-col">
                    <div className="text-default pb-2 text-lg font-medium">
                      <div className="inline-block">{metric.label}</div>
                    </div>
                    <MetricWithDelta
                      name={metric.key}
                      value={metricData?.[0] ?? null}
                      delta={delta}
                      doesCompareValueExist={doesCompareValueExist}
                      wrapperClassName="justify-left"
                      valueClassName="text-3xl font-semibold min-w-[40px] text-default"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <div className="flex w-full flex-row justify-end">
          <Combobox.Root
            options={intervalOptions}
            onChange={(value) => {
              handleIntervalChange(value as ChartInterval);
              analytics("analytics.chart.update", {
                time: value as ChartInterval,
              });
            }}
            value={query.chartInterval}
          >
            <Combobox.Trigger>
              <Combobox.SelectionButton
                title={selectedIntervalOption?.label ?? "Interval: "}
                isPlaceholder={!selectedIntervalOption}
              />
            </Combobox.Trigger>
            <Combobox.Popover layoutClassName="w-[90px]" align="end">
              <Combobox.Content />
            </Combobox.Popover>
          </Combobox.Root>
        </div>
        {isChartResultsLoading ? (
          <Skeleton className="h-[200px] w-full" />
        ) : (
          <div className="grid grid-cols-2 gap-4">
            {metricsToPlot.map((metricToPlot) => {
              const mainRangeMetrics =
                chartMainRangeResults[0]?.metrics[metricToPlot.key] ?? [];
              const compareAtRangeMetrics =
                chartCompareAtRangeResults[0]?.metrics[metricToPlot.key] ?? [];

              const hasNoChartData =
                mainRangeMetrics.every((value) => value === 0) &&
                compareAtRangeMetrics.every((value) => value === 0);

              return (
                <div
                  key={metricToPlot.key}
                  className="border-0.5 border-border flex flex-col space-y-6 rounded-xl p-6"
                >
                  <span className="typ-header-h3">{metricToPlot.label}</span>
                  {hasNoChartData ? (
                    <NoChartData />
                  ) : (
                    <ConditionallyBlurredComponent
                      shouldBlur={dataDisplayType === "partialData"}
                      blurSize="sm"
                      visibleComponent={
                        <AnalyticsEmptyState type="unlockAnalytics" />
                      }
                      componentToBlur={
                        <ReploLineChart
                          primaryPeriod={mainRangeMetrics}
                          secondaryPeriod={compareAtRangeMetrics}
                          ranges={chartQuery.ranges}
                          chartInterval={query.chartInterval}
                          timeZone={timeZone}
                          tooltipValueFormatter={(value) =>
                            formatValueToTableCell({
                              name: metricToPlot.key,
                              value,
                              currency: workspace.currency ?? "USD",
                              isFractional: query.isFractional,
                            })
                          }
                          className="h-[250px]"
                        />
                      }
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PageDetails;
